/**
 * Payment Methods Configuration Loader
 *
 * Dynamically loads country-specific payment method configuration.
 * Falls back to default if country-specific config doesn't exist.
 *
 * Usage:
 *   import { getPaymentMethodsConfig } from '@config/governance/paymentMethods';
 *   const config = getPaymentMethodsConfig('TW');
 *
 * To add a new country:
 *   1. Create a new file: paymentMethods/jp.js
 *   2. Export default config object
 *   3. Add to countryConfigs below
 *
 * The frontend will automatically use the new config without
 * changing any component code.
 */

import defaultConfig from './default';
import twConfig from './tw';
// import jpConfig from './jp';  // Add when needed

// Registry of country-specific configurations
const countryConfigs = {
  TW: twConfig,
  // JP: jpConfig,  // Add when needed
};

/**
 * Get payment methods configuration for a country
 *
 * @param {string} countryCode - ISO country code (e.g., 'TW', 'JP', 'US')
 * @returns {Object} Payment methods configuration
 */
export const getPaymentMethodsConfig = (countryCode) => {
  if (!countryCode) {
    return defaultConfig;
  }

  const upperCode = countryCode.toUpperCase();
  return countryConfigs[upperCode] || defaultConfig;
};

/**
 * Check if a payment method is available for a country
 *
 * @param {string} countryCode - ISO country code
 * @param {string} method - Payment method (e.g., 'credit_card', 'bank_transfer')
 * @returns {boolean}
 */
export const isPaymentMethodAvailable = (countryCode, method) => {
  const config = getPaymentMethodsConfig(countryCode);
  return config.availableMethods.includes(method);
};

/**
 * Get UI configuration for a country
 *
 * @param {string} countryCode - ISO country code
 * @returns {Object} UI configuration
 */
export const getPaymentMethodsUI = (countryCode) => {
  const config = getPaymentMethodsConfig(countryCode);
  return config.ui;
};

/**
 * Get UI configuration based on subscription context
 *
 * This function determines available payment methods based on:
 * - Country code
 * - Billing cycle (monthly/annual)
 * - Subscription amount
 *
 * Rules (per PAYMENT_FLOWS.md spec):
 * | Taiwan | 月繳 | 任意 | 信用卡 (唯一) |
 * | Taiwan | 年繳 | < NT$300,000 | 信用卡 (唯一) |
 * | Taiwan | 年繳 | >= NT$300,000 | ATM 匯款 (唯一) |
 * | International | 任意 | 任意 | 信用卡 (唯一) |
 *
 * @param {string} countryCode - ISO country code
 * @param {string} billingCycle - 'monthly' or 'annual'
 * @param {number} amount - Subscription amount
 * @returns {Object} UI configuration with payment method visibility
 */
export const getPaymentMethodsUIForSubscription = (countryCode, billingCycle, amount) => {
  const config = getPaymentMethodsConfig(countryCode);

  // If config has subscription-aware function, use it
  if (config.getUIForSubscription) {
    return config.getUIForSubscription(billingCycle, amount);
  }

  // Otherwise return default UI (international: credit card only)
  return {
    ...config.ui,
    onlyCreditCard: true,
    onlyBankTransfer: false,
  };
};

export default {
  getPaymentMethodsConfig,
  isPaymentMethodAvailable,
  getPaymentMethodsUI,
  getPaymentMethodsUIForSubscription,
};
